// ตรวจแผ่นก่อนบันทึก — ใช้ร่วมกันทั้งสแกนด้วยกล้องและสแกนจากไฟล์
//
// ครูเห็นภาพตัดเฉพาะข้อที่ตัวอ่านไม่แน่ใจ (ตั้งตรง ความละเอียดเต็ม) แล้วแตะตัวเลือกที่ถูก หรือกด "ถูกต้อง"
// ไม่ต้องอ่าน/แก้สตริงคำตอบยาวๆ อีกแล้ว บันทึกไม่ได้จนกว่าจะดูครบทุกข้อที่ไม่แน่ใจและรู้ว่าเป็นของใคร
import React, { useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap'
import { answerLabel, readerText, roomLabel } from './examUtils'
import { diffNos, savedAnswers } from './scanSession'
import SheetOverlay from './SheetOverlay'

const optionsOf = (q, choices) => (q.eq_type === 'truefalse'
  ? ['T', 'F']
  : Array.from({ length: Number(choices) || 4 }, (_, i) => String(i + 1)))

const reasonOf = (row) => {
  if (!row) return 'ไม่แน่ใจ'
  if (row.answer === '#') return 'ฝนหลายช่อง'
  if (row.answer === '') return 'รอยจาง — ตอบหรือไม่ตอบ?'
  return 'มีรอยอีกช่อง (ลบไม่สะอาด?)'
}

export default function ScanReview({
  item, exam, students, objective, labels, position, saving, discardLabel = 'ไม่บันทึก', onSave, onDiscard,
}) {
  const [studyId, setStudyId] = useState(item.studyId)
  const [answers, setAnswers] = useState(item.answers)
  const [confirmed, setConfirmed] = useState(() => new Set())
  const [showAll, setShowAll] = useState(false)
  const [showSheet, setShowSheet] = useState(item.spare || !!item.identity)

  const byNo = useMemo(() => new Map(objective.map((q) => [q.eq_no, q])), [objective])
  const rowOf = useMemo(() => new Map(item.rows.map((r) => [r.eq_no, r])), [item.rows])
  const student = students.find((s) => s.study_id === studyId)
  const saved = savedAnswers(student, objective)
  const unsure = new Set(item.uncertain)
  const changed = diffNos(answers, saved, objective).filter((no) => !unsure.has(no))
  const remaining = item.uncertain.filter((no) => !confirmed.has(no)).length
  const canSave = !!student && remaining === 0 && !saving

  const pick = (no, code) => {
    setAnswers((a) => ({ ...a, [no]: a[no] === code ? '' : code }))
    setConfirmed((c) => new Set(c).add(no))
  }
  const confirm = (no) => setConfirmed((c) => new Set(c).add(no))

  const bubbles = (q) => (
    <div className="d-flex flex-wrap align-items-center gap-2">
      {optionsOf(q, exam.exam_choices).map((code) => {
        const on = answers[q.eq_no] === code
        return (
          <Button key={code} variant={on ? 'primary' : 'outline-secondary'} className="rounded-circle p-0 fw-semibold"
            style={{ width: 42, height: 42 }} aria-pressed={on} aria-label={`ข้อ ${q.eq_no} ${answerLabel(code, labels)}`}
            onClick={() => pick(q.eq_no, code)}>
            {answerLabel(code, labels)}
          </Button>
        )
      })}
      {answers[q.eq_no] === '#' && <Badge bg="danger">ฝนหลายช่อง (ไม่ได้คะแนน)</Badge>}
      {answers[q.eq_no] === '' && <span className="fs-xs text-secondary">ไม่ตอบ</span>}
    </div>
  )

  const cropImg = (no) => item.crops?.[no] && (
    <img src={item.crops[no].url} alt={`ภาพข้อ ${no} บนกระดาษ`} className="d-block border rounded mb-2"
      style={{ maxWidth: '100%', width: item.crops[no].width, imageRendering: 'auto' }} />
  )

  const old = (no) => (saved ? answerLabel(saved[no], labels) || 'ไม่ตอบ' : '')

  return (
    <div className="mx-auto" style={{ maxWidth: 720 }}>
      <Card className="card-one mb-3">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 mb-2">
            <div className="fs-xs text-secondary me-auto">{item.label}</div>
            {position && <Badge bg="light" text="dark" className="border">แผ่นที่รอ {position.index}/{position.count}</Badge>}
          </div>

          {item.saveError && <Alert variant="danger" className="fs-sm py-2 mb-2">{item.saveError}</Alert>}
          {item.identity && <Alert variant="warning" className="fs-sm py-2 mb-2">{item.identity}</Alert>}

          {item.spare && (
            <Alert variant="info" className="fs-sm py-2 mb-2">
              กระดาษสำรอง — ฝนเลขประจำตัว <b>{item.idRead}</b>{student ? <> ตรงกับ <b>{student.st_name}</b> (เลขประจำตัว {String(student.st_idnumber || '').trim() || '-'})</> : null}
              <br />เทียบกับชื่อและเลขที่นักเรียนเขียนบนแผ่นก่อนบันทึก ถ้าไม่ใช่คนเดียวกันให้เลือกใหม่
              {item.idCrop && (
                <img src={item.idCrop.url} alt="ช่องฝนเลขประจำตัว" className="d-block border rounded mt-2 bg-white"
                  style={{ maxWidth: '100%', width: item.idCrop.width }} />
              )}
            </Alert>
          )}

          {item.studyId && !item.spare && !item.identity && student ? (
            <div className="fs-5 fw-semibold">
              {student.st_name}
              <span className="fs-6 text-secondary fw-normal"> · {roomLabel(student)} เลขที่ {student.seat}</span>
            </div>
          ) : (
            <Form.Select value={studyId ?? ''} onChange={(e) => setStudyId(Number(e.target.value) || null)}>
              <option value="">— เลือกนักเรียน —</option>
              {students.map((s) => (
                // เลขประจำตัวขึ้นก่อน — ครูเทียบกับเลขที่นักเรียนเขียน/ฝนบนกระดาษสำรอง
                <option key={s.study_id} value={s.study_id}>
                  {s.st_idnumber ? `${String(s.st_idnumber).trim()} · ` : ''}{roomLabel(s)} เลขที่ {s.seat} {s.st_name}{s.answers ? ' (มีคำตอบแล้ว)' : ''}
                </option>
              ))}
            </Form.Select>
          )}

          {item.dupe && (
            <Alert variant="warning" className="fs-sm py-2 mt-2 mb-0">
              ในไฟล์มีกระดาษของคนนี้มากกว่าหนึ่งแผ่น — บันทึกแผ่นที่ถูกแผ่นเดียว แผ่นที่เหลือกด "{discardLabel}"
            </Alert>
          )}
          {saved && (
            <Alert variant={changed.length || item.uncertain.length ? 'danger' : 'light'} className="fs-sm py-2 mt-2 mb-0">
              {student?.st_name} มีคำตอบที่บันทึกไว้แล้ว — บันทึกครั้งนี้จะแทนที่ของเดิม
              {changed.length > 0 && <> (ต่าง {changed.length} ข้อ)</>}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {item.uncertain.length > 0 && (
        <>
          <h6 className="mb-2">ข้อที่ต้องดู ({item.uncertain.length})</h6>
          {item.uncertain.map((no) => {
            const q = byNo.get(no)
            if (!q) return null
            const done = confirmed.has(no)
            return (
              <Card key={no} className={`mb-2 border-2 ${done ? 'border-success' : 'border-warning'}`}>
                <Card.Body className="py-2">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge rounded-pill bg-dark fs-6">{no}</span>
                    <Badge bg="warning" text="dark">{reasonOf(rowOf.get(no))}</Badge>
                    {saved && <span className="fs-xs text-secondary">เดิม: {old(no)}</span>}
                    <Button size="sm" className="ms-auto" variant={done ? 'success' : 'outline-success'} onClick={() => confirm(no)}>
                      <i className="ri-check-line"></i> {done ? 'ดูแล้ว' : 'ถูกต้อง'}
                    </Button>
                  </div>
                  {cropImg(no)}
                  {bubbles(q)}
                </Card.Body>
              </Card>
            )
          })}
        </>
      )}

      {changed.length > 0 && (
        <>
          <h6 className="mb-2 mt-3">ต่างจากที่บันทึกไว้ ({changed.length})</h6>
          {changed.map((no) => {
            const q = byNo.get(no)
            if (!q) return null
            return (
              <Card key={no} className="mb-2">
                <Card.Body className="py-2">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge rounded-pill bg-dark fs-6">{no}</span>
                    <span className="fs-sm">เดิม <b>{old(no)}</b> → สแกนได้ <b>{answerLabel(answers[no], labels) || 'ไม่ตอบ'}</b></span>
                  </div>
                  {cropImg(no)}
                  {bubbles(q)}
                </Card.Body>
              </Card>
            )
          })}
        </>
      )}

      <div className="d-flex flex-wrap gap-2 my-3">
        <Button variant="outline-secondary" size="sm" onClick={() => setShowAll((v) => !v)}>
          <i className="ri-list-check"></i> {showAll ? 'ซ่อนคำตอบทุกข้อ' : 'ดู / แก้คำตอบทุกข้อ'}
        </Button>
        {item.thumb && (
          <Button variant="outline-secondary" size="sm" onClick={() => setShowSheet((v) => !v)}>
            <i className="ri-image-line"></i> {showSheet ? 'ซ่อนภาพทั้งแผ่น' : 'ดูภาพทั้งแผ่น'}
          </Button>
        )}
      </div>

      {showSheet && item.thumb && (
        <div className="mb-3">
          <SheetOverlay thumb={item.thumb} markers={item.markers} rows={item.rows} text={readerText(answers, objective, labels)}
            objective={objective} uncertain={item.uncertain} idRows={item.idRows} labels={labels} />
        </div>
      )}

      {showAll && (
        <Card className="card-one mb-3">
          <Card.Body className="py-2">
            {objective.map((q) => (
              <div key={q.eq_no} className={`d-flex align-items-center gap-3 py-1 border-bottom ${unsure.has(q.eq_no) ? 'bg-warning-subtle' : ''}`}>
                <span className="fw-semibold text-end" style={{ width: 32 }}>{q.eq_no}</span>
                {bubbles(q)}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      <div className="d-flex gap-2 py-2 bg-body border-top" style={{ position: 'sticky', bottom: 0, zIndex: 5 }}>
        <Button variant="light" onClick={onDiscard} disabled={saving}>{discardLabel}</Button>
        <Button size="lg" className="flex-fill" disabled={!canSave} onClick={() => onSave(studyId, answers)}>
          {saving ? 'กำลังบันทึก…'
            : !student ? 'เลือกนักเรียนก่อน'
              : remaining > 0 ? `เหลือดู ${remaining} ข้อ`
                : <><i className="ri-check-line"></i> บันทึก</>}
        </Button>
      </div>
    </div>
  )
}
